import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";

import { missingVendorFields, PAYMENT_TERM_LABELS, vendorDetail, type VendorRequest } from "@ravnar/api-contract";

import { BankAccountCard } from "../-components/bank-account";
import { MissingDetails } from "../-components/missing-details";
import { NoAccess } from "../-components/no-access";
import { PartyDocuments } from "../-components/party-documents";
import { VendorForm } from "../-components/vendor-form";
import { ApiError } from "../../../api/client";
import { useAskAgain } from "../../../api/refresh";
import { sessionQueryOptions, useSession } from "../../../api/session";
import { removeVendor, updateVendor, vendorQueryOptions } from "../../../api/vendors";
import { PageHeader } from "../../../components/shell/app-shell";
import { Button } from "../../../components/ui/button";
import { buttonClassName } from "../../../components/ui/button-styles";
import { Card, CardHeader } from "../../../components/ui/card";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog";
import { showToast } from "../../../lib/toast";
import { DIRECTORY_DESCRIPTION } from "../index";

export const Route = createFileRoute("/directory/vendors/$vendorId")({
	loader: async ({ context, params }) => {
		// A member with no access to the Directory is told so by the page; asking the API would only answer 403.
		const session = await context.queryClient.query(sessionQueryOptions());
		if (session === null || session.member.access.directory === "none") {
			return;
		}
		try {
			return await context.queryClient.query({
				...vendorQueryOptions(params.vendorId),
				staleTime: "static",
			});
		} catch (error) {
			// Somebody removed it, or the id was typed by hand: the router's not-found page says so.
			if (error instanceof ApiError && error.status === 404) {
				notFound({ throw: true });
			}
			throw error;
		}
	},
	head: ({ loaderData }) => ({ meta: [{ title: `${loaderData?.name ?? "Vendor"} · Directory · Ravnar` }] }),
	staticData: { crumbs: ["Directory", "Vendor"] },
	component: VendorPage,
});

interface VendorDetailProps {
	/** Only an editor of the Directory may change the record, save the note, or remove the vendor. */
	readonly canEdit: boolean;
}

function VendorDetail({ canEdit }: VendorDetailProps) {
	const { vendorId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const { data: vendor } = useSuspenseQuery(vendorQueryOptions(vendorId));
	const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);
	const formId = useId();

	// A change to a vendor touches the kind's own queries and the audit log, which lives under "settings"; the
	// Directory that projects it and the entry the queue carries both land after the answer, so they are asked again.
	const refreshVendor = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: ["vendors"] }),
			queryClient.invalidateQueries({ queryKey: ["settings"] }),
			// Saving or removing a party closes the work item that asked for it, which the Work page and the rail count.
			queryClient.invalidateQueries({ queryKey: ["work"] }),
			askAgain(["directory"], ["settings", "audit-log"]),
		]);
	const saveMutation = useMutation({
		mutationFn: (input: VendorRequest) => updateVendor(vendorId, input),
		onSuccess: refreshVendor,
	});
	const removeMutation = useMutation({ mutationFn: () => removeVendor(vendorId), onSuccess: refreshVendor });

	const saveVendor = async (input: VendorRequest) => {
		try {
			await saveMutation.mutateAsync(input);
		} catch {
			// The alert under the form shows saveMutation.error.
			return;
		}
		showToast(`Changes to ${input.name} saved.`);
	};

	const removeVendorForGood = async () => {
		try {
			await removeMutation.mutateAsync();
		} catch {
			// The dialog's own error prop shows removeMutation.error, which is why it does not close itself.
			return;
		}
		setIsConfirmingRemoval(false);
		void navigate({ to: "/directory", search: {} });
		showToast(`${vendor.name} removed from the Directory.`);
	};

	return (
		<div className="max-w-[880px]">
			<PageHeader
				title={vendor.name}
				description={
					vendor.paymentTerms === undefined
						? vendorDetail(vendor)
						: `${vendorDetail(vendor)} · ${PAYMENT_TERM_LABELS[vendor.paymentTerms]}`
				}
				actions={
					<>
						<Link to="/directory" search={{}} className={buttonClassName({ size: "md" })}>
							Back to Directory
						</Link>
						{canEdit ? (
							<Button
								className="text-alert hover:text-alert"
								onClick={() => {
									setIsConfirmingRemoval(true);
								}}
							>
								Remove vendor
							</Button>
						) : null}
					</>
				}
			/>
			<MissingDetails missing={missingVendorFields(vendor)} noun="vendor" />
			<Card>
				<CardHeader title="Vendor" />
				<div className="p-4">
					<VendorForm key={vendor.id} formId={formId} vendor={vendor} canEdit={canEdit} onSave={saveVendor} />
				</div>
			</Card>
			{saveMutation.error === null ? null : (
				<p role="alert" className="mt-3.5 text-caption text-alert">
					{saveMutation.error.message}
				</p>
			)}
			{canEdit ? (
				<div className="mt-3.5 flex items-center justify-end gap-2">
					<Button type="submit" form={formId} variant="primary" disabled={saveMutation.isPending}>
						{saveMutation.isPending ? "Saving…" : "Save changes"}
					</Button>
				</div>
			) : null}
			<BankAccountCard
				vendorId={vendor.id}
				vendorName={vendor.name}
				holderName={vendor.legalName ?? vendor.name}
			/>
			<PartyDocuments kind="vendor" partyId={vendor.id} name={vendor.name} />
			<ConfirmDialog
				open={isConfirmingRemoval}
				title={`Remove ${vendor.name}?`}
				confirmLabel="Remove vendor"
				busy={removeMutation.isPending}
				error={removeMutation.error?.message}
				onConfirm={() => {
					void removeVendorForGood();
				}}
				onClose={() => {
					removeMutation.reset();
					setIsConfirmingRemoval(false);
				}}
			>
				Its record, its note and any bank account Finance pays it at are deleted for good. The audit log keeps
				that it happened.
			</ConfirmDialog>
		</div>
	);
}

function VendorPage() {
	const { member } = useSession();
	return member.access.directory === "none" ? (
		<NoAccess description={DIRECTORY_DESCRIPTION} />
	) : (
		<VendorDetail canEdit={member.access.directory === "editor"} />
	);
}
